import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      if (err?.response?.data?.detail && typeof err.response.data.detail === 'string') {
        setErrorMessage(err.response.data.detail);
      } else if (err?.response?.data?.message && typeof err.response.data.message === 'string') {
        setErrorMessage(err.response.data.message);
      } else if (err?.message && typeof err.message === 'string' && !err.message.includes('[object')) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Login failed. Please check your credentials.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-12">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-teal/5 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-mint/30 rounded-full blur-2xl" />
      </div>

      <div className="relative w-full max-w-md animate-fade-in">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal rounded-2xl mb-4 shadow-lg shadow-teal/20">
            <span className="text-2xl">🏪</span>
          </div>
          <h1 className="text-3xl font-bold text-teal-900 tracking-tight">
            Readora Seller Hub
          </h1>
          <p className="mt-2 text-base text-gray-500 font-medium">
            Grow your book business
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-black/5 border border-cream-200 p-8">
          {/* Error Alert */}
          {errorMessage && (
            <div className="mb-6 p-4 bg-coral-50 border border-coral-200 rounded-xl flex items-start gap-3 animate-slide-up">
              <span className="text-coral text-lg mt-0.5">⚠️</span>
              <p className="text-sm text-coral-700 font-medium leading-relaxed">{errorMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                  📧
                </span>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seller@example.com"
                  required
                  className="w-full pl-11 pr-4 py-3.5 bg-cream-50 border border-cream-300 rounded-xl 
                             text-gray-900 placeholder-gray-400 text-sm font-medium
                             focus:outline-none focus:ring-2 focus:ring-emerald/30 focus:border-emerald
                             transition-all duration-200 hover:border-gray-300"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                  🔒
                </span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full pl-11 pr-12 py-3.5 bg-cream-50 border border-cream-300 rounded-xl 
                             text-gray-900 placeholder-gray-400 text-sm font-medium
                             focus:outline-none focus:ring-2 focus:ring-emerald/30 focus:border-emerald
                             transition-all duration-200 hover:border-gray-300"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 
                             transition-colors text-sm"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="checkbox"
                aria-checked={rememberMe}
                onClick={() => setRememberMe(!rememberMe)}
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200
                  ${rememberMe 
                    ? 'bg-teal border-teal' 
                    : 'border-gray-300 hover:border-gray-400 bg-white'}`}
              >
                {rememberMe && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              <label 
                className="text-sm text-gray-600 font-medium cursor-pointer select-none"
                onClick={() => setRememberMe(!rememberMe)}
              >
                Remember me
              </label>
            </div>

            {/* Submit */}
            <button
              id="login-submit"
              type="submit"
              disabled={isLoading || !email || !password}
              className="w-full py-3.5 bg-teal hover:bg-teal-800 disabled:bg-teal/50 
                         text-white font-semibold text-sm rounded-xl
                         transition-all duration-200 transform hover:scale-[1.01] active:scale-[0.99]
                         shadow-lg shadow-teal/20 hover:shadow-xl hover:shadow-teal/30
                         disabled:cursor-not-allowed disabled:shadow-none disabled:transform-none
                         flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Signing in...</span>
                </>
              ) : (
                'Login to Seller Hub'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center animate-slide-up">
          <p className="text-sm text-gray-500 font-medium">
            Not a seller yet?
          </p>
          <p className="mt-1 text-sm text-emerald font-semibold">
            Download the Readora app to register
          </p>
        </div>

        {/* Powered by */}
        <p className="mt-6 text-center text-xs text-gray-400">
          © {new Date().getFullYear()} Readora · Seller Portal
        </p>
      </div>
    </div>
  );
};

export default Login;
